/*
 * ListItem.tsx  component
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useRef } from "react";
import { BEM } from "@adapptio/lib-components";
import { useForkRef } from "../hooks/useForkRef";
import { useEnhancedEffect } from "../hooks/useEnhancedEffect";
import { ButtonBase } from "./ButtonBase";

export interface IListItemProps extends React.ComponentPropsWithRef<"li"> {
	className?: string;
	autoFocus?: boolean;
	button?: boolean;
	disabled?: boolean;
	Component?: React.ElementType;
}

export const ListItem = React.forwardRef<HTMLLIElement, IListItemProps>(
	({ children, className, Component: ComponentProp, autoFocus, button, ...props }, ref) => {
		const listItemRef = useRef<HTMLLIElement>();
		const forkRef = useForkRef(listItemRef, ref);
		const baseClass = BEM("edt-list-item");

		const Component = button ? ButtonBase : ComponentProp;

		if (className) {
			baseClass.add(className);
		}

		const itemProps: Record<string, unknown> = {};

		if (button) {
			itemProps.Component = ComponentProp;
		}

		useEnhancedEffect(() => {
			if (autoFocus) {
				if (listItemRef.current) {
					listItemRef.current.focus();
				}
			}
		}, [ autoFocus ]);

		return (
			<Component className={baseClass.toClassName()} ref={forkRef} {...itemProps} {...props}>
				{children}
			</Component>
		);
	}
);

ListItem.defaultProps = {
	Component: "li",
	button: false
};
